import {Injectable, Injector, inject} from '@angular/core'
import {FeatureFlags} from '@models/feature-flags.models'
import {EmbeddedStoreService} from '@service/embeddingService/embedded-store.service'
import {FeatureFlagsService} from './feature-flags.service'

/**
 * Variante de `FeatureFlagsService` para el modo embebido.
 *
 * @description
 * En el bundle embebido los flags no se evalúan directamente contra Split.io,
 * sino que los envía el host (Connecta-UX u otro contenedor) a través de
 * `EmbeddedStoreService.featureFlags`. Esta subclase sobrescribe
 * `getTreatment` para leer de ese `Map` y devolver `false` si el flag no
 * está presente.
 *
 * Capa de Aplicación: delega la resolución en el host, evitando una
 * dependencia directa con el SDK de Split dentro de la iframe embebida.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * Registrado sólo en el bundle embebido:
 *
 * ```typescript
 * {provide: FeatureFlagsService, useClass: FeatureFlagsEmbeddedService}
 * ```
 *
 * ### Best Practices
 *
 * - El host debe enviar todos los flags relevantes durante el `peerPayload`
 *   inicial; cualquier flag ausente se resuelve como `false`.
 * - No usar `getTreatmentSignal` en el bundle embebido: al no inyectar Split
 *   no se propagan cambios en caliente (el signal devuelto queda estático).
 *
 * ### Bad Uses / Errores comunes
 *
 * - Asumir que los flags se auto-actualizan en modo embebido: requieren que
 *   el host reenvíe el payload.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsEmbeddedService extends FeatureFlagsService {
  // Resolución lazy: EmbeddedStoreService inyecta ApiService, que en modo embebido depende de FeatureFlagsService — ciclo DI.
  private readonly injector = inject(Injector)
  override getTreatment = (name: FeatureFlags): boolean =>
    this.injector.get(EmbeddedStoreService).featureFlags.get(name) ?? false
}
